import { html } from "lit";
import { customElement, property, query } from "lit/decorators.js";

import Chart from "chart.js/auto";
import pattern from "patternomaly";

import { Watch } from "@/decorators/watch";

import AtlasElement, { type AtlasElementProps } from "@/components/atlas-element";

import type { ChartPatternType } from "../chart-patterns";
import type { ChartData, DataType } from "./types";

import colorStyles from "../chart-colors.scss";

import styles from "./atlas-column-chart.scss";

import { externalLegendHandler, externalTooltipHandler, columnChartBaseOptions } from "../chart-config";

export type ColumnChartProps = AtlasElementProps & {
    "x-axis-key": string;
    "y-axis-key": string;
    "order-keys": Array<string>;
    "max-y-value": number;
    "data": ChartData;
    "data-type": DataType;
};

/**
 * @tag atlas-column-chart
 */
@customElement("atlas-column-chart")
export default class AtlasColumnChart extends AtlasElement {
    static styles = styles;

    /** Propriedade que define a key utilizada para os valores do eixo X */
    @property({ type: String, attribute: "x-axis-key" }) xAxisKey: string = "key";

    /** Propriedade que define a key utilizada para os valores do eixo Y */
    @property({ type: String, attribute: "y-axis-key" }) yAxisKey: string = "value";

    /** Propriedade que define a ordem de pintura das chaves que se referem aos dados de entrada */
    @property({ type: Array, attribute: "order-keys" }) orderKeys: Array<string>;

    /** Propriedade que contém os dados de entrada */
    @property({ type: Array }) data: ChartData;

    /** Propriedade que define qual o valor máximo no eixo Y */
    @property({ type: Number, attribute: "max-y-value" }) maxYValue: number;

    /** Propriedade que define qual o valor máximo no eixo Y */
    @property({ type: String, attribute: "data-type" }) dataType: DataType = "monetary";

    @query(".legend-list")
    public legendList: HTMLElement;

    @query(".legend-item")
    public legendItemTemplate: HTMLElement;

    @query(".chart")
    public chartElement: HTMLCanvasElement;

    private _chartObject: Chart;

    private _chartBackgroundColors: { [key: string]: string } = {};

    /** @internal */
    public connectedCallback(): void {
        super.connectedCallback?.();

        this.buildColorsFromCSS();

        this.updateComplete.then(() => {
            this.buildChart();
        });
    }

    /** @internal */
    public disconnectedCallback(): void {
        super.disconnectedCallback?.();
    }

    private buildColorsFromCSS() {
        const { cssRules } = colorStyles.styleSheet;

        const colors: Record<string, string> = {};

        for (const rule of cssRules) {
            const key = (rule as CSSStyleRule).selectorText;
            const value = (rule as CSSStyleRule).style.getPropertyValue("color");

            colors[key] = value;
        }

        this._chartBackgroundColors = colors;
    }

    private buildOptions() {
        const options = columnChartBaseOptions;

        options.parsing = {
            xAxisKey: this.xAxisKey,
            yAxisKey: this.yAxisKey
        };

        options.plugins = {
            // @ts-expect-error
            externalLegendHandler: {},
            legend: {
                display: false
            },
            tooltip: {
                enabled: false,
                position: "average",
                external: externalTooltipHandler
            }
        };

        options.scales.y.max = this.maxYValue;

        return options;
    }

    private createPattern(backgroundPattern: ChartPatternType, backgroundColor: string) {
        return pattern.draw(backgroundPattern, backgroundColor);
    }

    private getDataBackground(backgroundPattern: ChartPatternType, backgroundColor: string) {
        const backgroundColorRGB = this._chartBackgroundColors[backgroundColor] || backgroundColor;

        if (backgroundPattern) {
            return this.createPattern(backgroundPattern, backgroundColorRGB);
        }

        return backgroundColorRGB;
    }

    private buildData() {
        if (!this.data) return {};

        if (!this.orderKeys) {
            this.orderKeys = Object.keys(this.data);
        }

        const chartData = this.orderKeys.map((key: string) => {
            const columnData = this.data[key as keyof ChartData];

            if (!columnData) return null;

            const { label, backgroundColor, backgroundPattern, order, data } = columnData;

            return {
                type: "bar",
                label,
                backgroundColor: this.getDataBackground(backgroundPattern, backgroundColor),
                order,
                data
            };
        }).filter(Boolean);

        return {
            datasets: chartData
        };
    }

    /** @internal */
    @Watch("maxYValue", true)
    public updateMaxValue() {
        if (!this._chartObject) return;

        this._chartObject.options.scales.y.max = this.maxYValue;
        this._chartObject.update();
    }

    /** @internal */
    @Watch(["data", "xAxisKey, yAxisKey, orderKeys"], true)
    public buildChart() {
        if (this._chartObject) this._chartObject.destroy();

        const config: any = {
            data: this.buildData(),
            options: this.buildOptions(),
            plugins: [externalLegendHandler]
        };

        this._chartObject = new Chart(this.chartElement, config);
    }

    private renderLegendItemTemplate() {
        return html`
            <li class="legend-item" hidden>
                <canvas class="legend-color"></canvas>
                <span class="legend-text"></span>
            </li>
        `;
    }

    private renderLegend() {
        return html`
            <ul class="legend-list"></ul>

            ${this.renderLegendItemTemplate()}
        `;
    }

    private renderTooltip() {
        return html`
            <div class="atlas-chart-tooltip" role="tooltip" aria-labelledby="tooltip-slot">
                <div class="tooltip-inner"></div>
            </div>
        `;
    }

    /** @intenal */
    public render() {
        return html`
            ${this.renderLegend()}
            <div class="chart-container">
                <canvas class="chart"></canvas>
                ${this.renderTooltip()}
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-column-chart": AtlasColumnChart;
    }
}
